import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress
} from '@mui/material';
import { formatCurrency } from '../utils/format';
import publicApi from '../services/publicApi';

const RangeBar = ({ min, max, current, label }) => {
  if (!min || !max || !current) {
    return (
      <Box sx={{ mb: 1.5, direction: 'ltr' }}>
        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.75rem' }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          No data available
        </Typography>
      </Box>
    );
  }

  // Calculate position of current price on the range (0-100%)
  const range = max - min;
  const position = range > 0 ? ((current - min) / range) * 100 : 50;
  
  return (
    <Box sx={{ mb: 1.5, direction: 'ltr' }}>
      {/* Label */}
      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.75rem' }}>
        {label}
      </Typography>
      
      {/* Price Range Numbers */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, direction: 'ltr' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {formatCurrency(min)}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {formatCurrency(max)}
        </Typography>
      </Box>
      
      {/* Range Bar */}
      <Box sx={{ position: 'relative', height: 4, direction: 'ltr' }}>
        {/* Background bar */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            backgroundColor: '#e0e0e0',
            borderRadius: 1
          }} 
        />
        
        {/* Current price indicator */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: -2,
            left: `${Math.max(0, Math.min(100, position))}%`,
            transform: 'translateX(-50%)',
            width: 8,
            height: 8,
            backgroundColor: '#000',
            borderRadius: '50%',
            zIndex: 2
          }} 
        />
      </Box>
    </Box>
  );
};

const KeyStatistics = ({ coinSymbol }) => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      if (!coinSymbol) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log(`[KeyStatistics] Fetching statistics for ${coinSymbol}`);
        const response = await publicApi.get(`/api/public/coins/${coinSymbol}/key-statistics`);
        setStatistics(response.data);
        console.log(`[KeyStatistics] Got statistics:`, response.data);
      } catch (err) {
        console.error(`[KeyStatistics] Error fetching statistics:`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [coinSymbol]);

  if (loading) {
    return (
      <Paper elevation={1} sx={{ p: 2, mb: 2, direction: 'ltr' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 40 }}>
          <CircularProgress size={16} />
        </Box>
      </Paper>
    );
  }

  if (error || !statistics) {
    return null; // Don't show anything if there's an error or no data
  }

  return (
    <>
      <Paper elevation={1} sx={{ p: 2, mb: 2, direction: 'ltr' }}>
        {/* Header */}
        <Box sx={{ mb: 1.5, direction: 'ltr' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.secondary' }}>
            KEY STATISTICS
          </Typography>
        </Box>

        {/* Statistics */}
        <RangeBar 
          min={statistics.dayRange?.min} 
          max={statistics.dayRange?.max} 
          current={statistics.currentPrice}
          label="Day's Range"
        />
        
        <RangeBar 
          min={statistics.weekRange?.min} 
          max={statistics.weekRange?.max} 
          current={statistics.currentPrice}
          label="7 Day Range"
        />
        
        <RangeBar 
          min={statistics.monthRange?.min} 
          max={statistics.monthRange?.max} 
          current={statistics.currentPrice}
          label="30 Day Range"
        />
        
        <RangeBar 
          min={statistics.yearRange?.min} 
          max={statistics.yearRange?.max} 
          current={statistics.currentPrice}
          label="52 Week Range"
        />
      </Paper>
    </>
  );
};

export default KeyStatistics;